import { useCallback, useEffect, useState, useMemo } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
    Download, Upload, FileText, AlertTriangle, Loader2, CalendarDays, X, Receipt,
} from "lucide-react";
import { MoneyInput } from "@/components/MoneyInput";

const MAX_BYTES = 4 * 1024 * 1024;

function fmtDate(iso) {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString(); } catch { return iso; }
}

function expiryStatus(expiry) {
    if (!expiry) return { label: "—", cls: "text-ink/40", urgent: false };
    const exp = new Date(expiry);
    const now = new Date();
    const days = Math.ceil((exp - now) / (1000 * 60 * 60 * 24));
    if (days < 0) return { label: `Expired ${Math.abs(days)}d ago`, cls: "text-red-700 font-medium", urgent: true };
    if (days <= 30) return { label: `Expires in ${days}d`, cls: "text-amber-700 font-medium", urgent: true };
    if (days <= 60) return { label: `Expires in ${days}d`, cls: "text-amber-600", urgent: false };
    return { label: fmtDate(expiry), cls: "text-ink/60", urgent: false };
}

export default function MyDocuments() {
    const { user } = useAuth();
    const [docs, setDocs] = useState([]);
    const [types, setTypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploadOpen, setUploadOpen] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [d, t] = await Promise.all([
                api.get("/documents", { params: user?.employee_id ? { employee_id: user.employee_id } : {} }),
                api.get("/documents/types"),
            ]);
            setDocs(d.data || []);
            setTypes(t.data || []);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Failed to load documents");
        } finally {
            setLoading(false);
        }
    }, [user?.employee_id]);

    useEffect(() => { load(); }, [load]);

    const onDownload = async (doc) => {
        try {
            const r = await api.get(`/documents/${doc.id}/download`, { responseType: "blob" });
            const url = window.URL.createObjectURL(r.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = doc.file_name || "document.pdf";
            a.click();
            window.URL.revokeObjectURL(url);
        } catch (e) {
            toast.error("Download failed");
        }
    };

    const urgentDocs = useMemo(() => docs.filter((d) => {
        if (!d.expiry_date) return false;
        const days = Math.ceil((new Date(d.expiry_date) - new Date()) / (1000 * 60 * 60 * 24));
        return days <= 30;
    }), [docs]);

    return (
        <div className="px-5 sm:px-8 md:px-12 py-8 sm:py-12 md:py-16" data-testid="my-documents-page">
            <div className="flex items-end justify-between flex-wrap gap-6">
                <div>
                    <div className="overline">Self-service</div>
                    <h1 className="mt-3 font-display font-black tracking-tighter text-4xl sm:text-5xl text-ink leading-[1.02]">
                        My documents
                    </h1>
                    <p className="mt-3 text-sm text-ink/60 max-w-xl">
                        Personal files HR has on your record — plus rent receipts and certifications you upload yourself for tax relief or compliance.
                    </p>
                </div>
                <button
                    onClick={() => setUploadOpen(true)}
                    className="inline-flex items-center gap-2 bg-klein text-white px-5 py-3 text-sm hover:bg-klein-500"
                    data-testid="my-documents-upload-btn"
                >
                    <Upload className="w-4 h-4" /> Upload a document
                </button>
            </div>

            {urgentDocs.length > 0 && (
                <div className="mt-8 border border-amber-300 bg-amber-50/70 text-amber-900 px-4 py-3 text-sm flex items-start gap-3" data-testid="my-documents-expiring-banner">
                    <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                    <div>
                        <strong>{urgentDocs.length}</strong> document{urgentDocs.length === 1 ? "" : "s"} need attention soon.
                        Please re-upload before {fmtDate(urgentDocs[0].expiry_date)} to keep your records current.
                    </div>
                </div>
            )}

            <div className="mt-10 bg-white border border-ink/10 overflow-auto">
                {loading ? (
                    <div className="py-16 text-center text-sm text-ink/40">
                        <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading documents…
                    </div>
                ) : docs.length === 0 ? (
                    <div className="py-16 text-center text-sm text-ink/40">
                        <FileText className="w-8 h-8 mx-auto mb-3 text-ink/30" />
                        No documents on file yet. Use &quot;Upload a document&quot; to get started.
                    </div>
                ) : (
                    <table className="w-full border-collapse text-sm">
                        <thead>
                            <tr className="border-b-2 border-ink/20">
                                <th className="text-left overline px-4 py-3">Type</th>
                                <th className="text-left overline px-4 py-3 hidden md:table-cell">File</th>
                                <th className="text-left overline px-4 py-3 hidden lg:table-cell">Issued</th>
                                <th className="text-left overline px-4 py-3">Expiry</th>
                                <th className="text-left overline px-4 py-3 hidden md:table-cell">Uploaded</th>
                                <th className="text-right overline px-4 py-3">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {docs.map((d) => {
                                const exp = expiryStatus(d.expiry_date);
                                return (
                                    <tr key={d.id} className="border-b border-ink/10 hover:bg-bone/40" data-testid={`my-doc-row-${d.id}`}>
                                        <td className="px-4 py-3 font-medium text-ink">{d.doc_type_label || d.doc_type}</td>
                                        <td className="px-4 py-3 text-ink/60 hidden md:table-cell truncate max-w-[260px]">{d.file_name}</td>
                                        <td className="px-4 py-3 text-ink/60 hidden lg:table-cell">{fmtDate(d.issued_date)}</td>
                                        <td className={`px-4 py-3 ${exp.cls}`}>
                                            {d.expiry_date ? (
                                                <span className="inline-flex items-center gap-1.5">
                                                    {exp.urgent && <AlertTriangle className="w-3.5 h-3.5" />}
                                                    {exp.label}
                                                </span>
                                            ) : <span className="text-ink/30">—</span>}
                                        </td>
                                        <td className="px-4 py-3 text-ink/50 hidden md:table-cell">{fmtDate(d.uploaded_at)}</td>
                                        <td className="px-4 py-3 text-right">
                                            <button
                                                onClick={() => onDownload(d)}
                                                data-testid={`my-doc-download-${d.id}`}
                                                className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-ink/15 text-xs hover:bg-ink/5"
                                            >
                                                <Download className="w-3.5 h-3.5" /> Download
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>

            {uploadOpen && (
                <UploadDialog
                    employeeId={user?.employee_id}
                    types={types}
                    onClose={() => setUploadOpen(false)}
                    onUploaded={() => { setUploadOpen(false); load(); }}
                />
            )}
        </div>
    );
}

function UploadDialog({ employeeId, types, onClose, onUploaded }) {
    const [docType, setDocType] = useState("rent_receipt");
    const [issuedDate, setIssuedDate] = useState("");
    const [expiryDate, setExpiryDate] = useState("");
    const [annualRent, setAnnualRent] = useState("");
    const [notes, setNotes] = useState("");
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);

    const selectedType = types.find((t) => t.id === docType);
    const needsExpiry = selectedType?.has_expiry;
    const isRent = docType === "rent_receipt";

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!file) { toast.error("Choose a PDF first"); return; }
        if (file.size > MAX_BYTES) { toast.error("File too large (max 4MB)"); return; }
        if (!file.name.toLowerCase().endsWith(".pdf")) { toast.error("Only PDF files accepted"); return; }
        if (isRent) {
            if (!expiryDate) { toast.error("Rent receipt requires an expiry date"); return; }
            if (!annualRent || Number(annualRent) <= 0) {
                toast.error("Enter the annual rent amount on the receipt — it sets your PAYE tax relief");
                return;
            }
        }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const paramsObj = {
                employee_id: employeeId,
                doc_type: docType,
                issued_date: issuedDate,
                expiry_date: expiryDate,
                notes: notes,
            };
            if (isRent) paramsObj.annual_rent = String(Number(annualRent));
            const qs = new URLSearchParams(paramsObj).toString();
            await api.post(`/documents?${qs}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success(isRent
                ? "Rent receipt uploaded — sent to Accountant for approval. PAYE relief will activate once approved."
                : "Document uploaded");
            onUploaded();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="my-doc-upload-dialog">
            <form onSubmit={onSubmit} className="bg-white max-w-lg w-full max-h-[90vh] overflow-auto">
                <div className="flex items-center justify-between p-6 border-b border-ink/10">
                    <div>
                        <div className="overline">Upload</div>
                        <h2 className="font-display text-2xl tracking-tighter mt-1">New document</h2>
                    </div>
                    <button type="button" onClick={onClose} className="p-2 hover:bg-ink/5" data-testid="my-doc-upload-close">
                        <X className="w-4 h-4" />
                    </button>
                </div>
                <div className="p-6 space-y-5">
                    <div>
                        <label className="overline">Document type</label>
                        <select
                            value={docType} onChange={(e) => setDocType(e.target.value)}
                            data-testid="my-doc-type"
                            className="mt-2 w-full border border-ink/20 px-3 py-2.5 text-sm focus:border-klein focus:outline-none"
                        >
                            {types.map((t) => (
                                <option key={t.id} value={t.id}>{t.label}</option>
                            ))}
                        </select>
                        {docType === "rent_receipt" && (
                            <p className="text-xs text-emerald-700 mt-1 inline-flex items-center gap-1">
                                <Receipt className="w-3 h-3" /> Will be reviewed by HR/Accountant for PAYE rent relief.
                            </p>
                        )}
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="overline">Issued date</label>
                            <input
                                type="date" value={issuedDate} onChange={(e) => setIssuedDate(e.target.value)}
                                data-testid="my-doc-issued"
                                className="mt-2 w-full border border-ink/20 px-3 py-2.5 text-sm focus:border-klein focus:outline-none"
                            />
                        </div>
                        {needsExpiry && (
                            <div>
                                <label className="overline">Expiry date</label>
                                <input
                                    type="date" value={expiryDate} onChange={(e) => setExpiryDate(e.target.value)}
                                    data-testid="my-doc-expiry"
                                    className="mt-2 w-full border border-ink/20 px-3 py-2.5 text-sm focus:border-klein focus:outline-none"
                                />
                            </div>
                        )}
                    </div>
                    {isRent && (
                        <div className="bg-amber-50 border border-amber-200 text-amber-900 text-xs p-3" data-testid="my-doc-rent-warning">
                            Rent receipts go to your Accountant for verification first. They&apos;ll appear in your documents and activate your PAYE rent relief only after approval.
                        </div>
                    )}
                    {isRent && (
                        <div>
                            <label className="overline">Annual rent on receipt (₦) *</label>
                            <MoneyInput
                                value={annualRent}
                                onChange={(e) => setAnnualRent(e.target.value)}
                                placeholder="e.g. 1,200,000"
                                data-testid="my-doc-annual-rent"
                                className="mt-2 w-full border border-ink/20 px-3 py-2.5 text-sm focus:border-klein focus:outline-none"
                            />
                            <p className="text-xs text-ink/50 mt-1.5">Sets your PAYE tax relief — the lower of 20% × annual rent or ₦500,000/year.</p>
                        </div>
                    )}
                    <div>
                        <label className="overline">File (PDF, max 4MB)</label>
                        <input
                            type="file" accept="application/pdf"
                            onChange={(e) => setFile(e.target.files?.[0] || null)}
                            data-testid="my-doc-file"
                            className="mt-2 w-full text-sm"
                        />
                        {file && (
                            <p className="text-xs text-ink/50 mt-1">
                                <CalendarDays className="w-3 h-3 inline mr-1" /> {file.name} · {(file.size / 1024).toFixed(0)} KB
                            </p>
                        )}
                    </div>
                    <div>
                        <label className="overline">Notes (optional)</label>
                        <textarea
                            value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
                            data-testid="my-doc-notes"
                            className="mt-2 w-full border border-ink/20 px-3 py-2 text-sm focus:border-klein focus:outline-none"
                            placeholder="Any context for HR…"
                        />
                    </div>
                </div>
                <div className="flex items-center justify-end gap-3 p-6 border-t border-ink/10 bg-bone/30">
                    <button type="button" onClick={onClose} className="px-5 py-2.5 text-sm hover:bg-ink/5">
                        Cancel
                    </button>
                    <button
                        type="submit" disabled={uploading || !file}
                        data-testid="my-doc-upload-submit"
                        className="inline-flex items-center gap-2 bg-klein text-white px-5 py-2.5 text-sm hover:bg-klein-500 disabled:opacity-60"
                    >
                        {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                        Upload
                    </button>
                </div>
            </form>
        </div>
    );
}
